import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertCircle, Loader2, Plus } from 'lucide-react';
import { apiClient } from '../../../lib/api';
import type { Company, FinancialStatement } from './types';
import { fmtMoney } from './types';

/**
 * Financial Statements tab.
 *
 * Layout: pick a company → see all its yearly statements side-by-side as
 * columns (most recent on the left in RTL). "Add year" reveals a long form
 * that mirrors the schema sections — Income Statement, Balance Sheet,
 * Working Capital. All values are in thousands of ILS.
 *
 * Updates aren't supported by the backend yet; the form is create-only.
 */

type NumStr = string;

type FormState = {
  fiscal_year: NumStr;
  is_audited: boolean;

  // Income statement
  revenue: NumStr;
  gross_profit: NumStr;
  operating_profit: NumStr;
  net_profit: NumStr;
  depreciation: NumStr;
  financing_expense: NumStr;
  tax_expense: NumStr;
  dividend: NumStr;

  // Balance sheet — assets
  total_assets: NumStr;
  current_assets: NumStr;
  cash: NumStr;
  receivables: NumStr;
  inventory: NumStr;
  fixed_assets: NumStr;
  intangible_assets: NumStr;
  leasehold_improvements: NumStr;

  // Balance sheet — liabilities & equity
  payables: NumStr;
  short_term_debt: NumStr;
  long_term_debt: NumStr;
  current_maturities: NumStr;
  shareholder_liabilities: NumStr;
  related_party_liabilities: NumStr;
  total_equity: NumStr;

  // Working capital
  working_capital_customer_advances: NumStr;
  working_capital_supplier_advances: NumStr;
  working_capital_prepaid_expenses: NumStr;
  working_capital_deferred_income: NumStr;
  working_capital_accrued_expenses: NumStr;
  working_capital_accrued_income: NumStr;

  notes: NumStr;
};

const empty: FormState = {
  fiscal_year: new Date().getFullYear().toString(),
  is_audited: false,
  revenue: '',
  gross_profit: '',
  operating_profit: '',
  net_profit: '',
  depreciation: '',
  financing_expense: '',
  tax_expense: '',
  dividend: '',
  total_assets: '',
  current_assets: '',
  cash: '',
  receivables: '',
  inventory: '',
  fixed_assets: '',
  intangible_assets: '',
  leasehold_improvements: '',
  payables: '',
  short_term_debt: '',
  long_term_debt: '',
  current_maturities: '',
  shareholder_liabilities: '',
  related_party_liabilities: '',
  total_equity: '',
  working_capital_customer_advances: '',
  working_capital_supplier_advances: '',
  working_capital_prepaid_expenses: '',
  working_capital_deferred_income: '',
  working_capital_accrued_expenses: '',
  working_capital_accrued_income: '',
  notes: '',
};

const N = (v: string) => (v === '' ? null : v);

export function FinancialsTab({ caseId }: { caseId: string }) {
  const qc = useQueryClient();
  const [selectedCompanyId, setSelectedCompanyId] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState<FormState>(empty);

  const companiesQuery = useQuery<Company[]>({
    queryKey: ['companies', caseId],
    queryFn: () => apiClient.get<Company[]>(`/companies?case_id=${caseId}`),
  });

  const companies = companiesQuery.data ?? [];
  const activeCompanyId =
    selectedCompanyId ?? companies.find((c) => c.is_primary)?.id ?? companies[0]?.id ?? null;

  const statementsQuery = useQuery<FinancialStatement[]>({
    queryKey: ['financials', activeCompanyId],
    queryFn: () =>
      apiClient.get<FinancialStatement[]>(`/financials?company_id=${activeCompanyId}`),
    enabled: !!activeCompanyId,
  });

  const create = useMutation<FinancialStatement, Error, FormState>({
    mutationFn: (s) => {
      if (!activeCompanyId) throw new Error('בחר חברה');
      return apiClient.post<FinancialStatement>('/financials', {
        company_id: activeCompanyId,
        fiscal_year: Number(s.fiscal_year),
        is_audited: s.is_audited,
        revenue: N(s.revenue),
        gross_profit: N(s.gross_profit),
        operating_profit: N(s.operating_profit),
        net_profit: N(s.net_profit),
        depreciation: N(s.depreciation),
        financing_expense: N(s.financing_expense),
        tax_expense: N(s.tax_expense),
        dividend: N(s.dividend),
        total_assets: N(s.total_assets),
        current_assets: N(s.current_assets),
        cash: N(s.cash),
        receivables: N(s.receivables),
        inventory: N(s.inventory),
        fixed_assets: N(s.fixed_assets),
        intangible_assets: N(s.intangible_assets),
        leasehold_improvements: N(s.leasehold_improvements),
        payables: N(s.payables),
        short_term_debt: N(s.short_term_debt),
        long_term_debt: N(s.long_term_debt),
        current_maturities: N(s.current_maturities),
        shareholder_liabilities: N(s.shareholder_liabilities),
        related_party_liabilities: N(s.related_party_liabilities),
        total_equity: N(s.total_equity),
        working_capital_customer_advances: N(s.working_capital_customer_advances),
        working_capital_supplier_advances: N(s.working_capital_supplier_advances),
        working_capital_prepaid_expenses: N(s.working_capital_prepaid_expenses),
        working_capital_deferred_income: N(s.working_capital_deferred_income),
        working_capital_accrued_expenses: N(s.working_capital_accrued_expenses),
        working_capital_accrued_income: N(s.working_capital_accrued_income),
        notes: s.notes || null,
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['financials', activeCompanyId] });
      setForm(empty);
      setAdding(false);
    },
  });

  if (companies.length === 0 && companiesQuery.isFetched) {
    return (
      <div className="card text-center text-sm text-slate-600 py-8">
        עוד לא הוזנו חברות בקייס. עבור לטאב "חברות" והוסף לפחות חברה אחת.
      </div>
    );
  }

  const statements = statementsQuery.data ?? [];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-2">
          <label className="text-sm text-slate-600">חברה:</label>
          <select
            value={activeCompanyId ?? ''}
            onChange={(e) => setSelectedCompanyId(e.target.value)}
            className="input !w-auto"
          >
            {companies.map((c) => (
              <option key={c.id} value={c.id}>
                {c.short_label}
                {c.is_primary ? ' ★' : ''}
              </option>
            ))}
          </select>
        </div>
        {!adding && (
          <button
            type="button"
            onClick={() => {
              setForm(empty);
              setAdding(true);
            }}
            disabled={!activeCompanyId}
            className="btn-primary inline-flex items-center gap-2 disabled:opacity-50"
          >
            <Plus className="w-4 h-4" />
            דוח שנה
          </button>
        )}
      </div>

      {adding && (
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-navy">דוח שנה חדש (אלפי ₪)</h3>
            <label className="inline-flex items-center gap-2 text-sm text-slate-600">
              <input
                type="checkbox"
                checked={form.is_audited}
                onChange={(e) => setForm({ ...form, is_audited: e.target.checked })}
                className="rounded"
              />
              דוח מבוקר
            </label>
          </div>

          <div className="grid md:grid-cols-4 gap-4 mb-6">
            <NumField
              label="שנת דוח"
              value={form.fiscal_year}
              onChange={(v) => setForm({ ...form, fiscal_year: v })}
            />
          </div>

          <Section title="דוח רווח והפסד">
            <NumField label="הכנסות" value={form.revenue} onChange={(v) => setForm({ ...form, revenue: v })} />
            <NumField label="רווח גולמי" value={form.gross_profit} onChange={(v) => setForm({ ...form, gross_profit: v })} />
            <NumField label="רווח תפעולי" value={form.operating_profit} onChange={(v) => setForm({ ...form, operating_profit: v })} />
            <NumField label="רווח נקי" value={form.net_profit} onChange={(v) => setForm({ ...form, net_profit: v })} />
            <NumField label="פחת" value={form.depreciation} onChange={(v) => setForm({ ...form, depreciation: v })} />
            <NumField label="הוצאות מימון" value={form.financing_expense} onChange={(v) => setForm({ ...form, financing_expense: v })} />
            <NumField label="מס" value={form.tax_expense} onChange={(v) => setForm({ ...form, tax_expense: v })} />
            <NumField label="דיבידנד" value={form.dividend} onChange={(v) => setForm({ ...form, dividend: v })} />
          </Section>

          <Section title="מאזן — נכסים">
            <NumField label="סה״כ מאזן" value={form.total_assets} onChange={(v) => setForm({ ...form, total_assets: v })} />
            <NumField label="רכוש שוטף" value={form.current_assets} onChange={(v) => setForm({ ...form, current_assets: v })} />
            <NumField label="מזומנים" value={form.cash} onChange={(v) => setForm({ ...form, cash: v })} />
            <NumField label="לקוחות" value={form.receivables} onChange={(v) => setForm({ ...form, receivables: v })} />
            <NumField label="מלאי" value={form.inventory} onChange={(v) => setForm({ ...form, inventory: v })} />
            <NumField label="רכוש קבוע" value={form.fixed_assets} onChange={(v) => setForm({ ...form, fixed_assets: v })} />
            <NumField label="נכסים בלתי מוחשיים" value={form.intangible_assets} onChange={(v) => setForm({ ...form, intangible_assets: v })} />
            <NumField label="שיפורים במושכר" value={form.leasehold_improvements} onChange={(v) => setForm({ ...form, leasehold_improvements: v })} />
          </Section>

          <Section title="מאזן — התחייבויות והון">
            <NumField label="ספקים" value={form.payables} onChange={(v) => setForm({ ...form, payables: v })} />
            <NumField label="חוב בנק ז״ק" value={form.short_term_debt} onChange={(v) => setForm({ ...form, short_term_debt: v })} />
            <NumField label="חוב בנק ז״א" value={form.long_term_debt} onChange={(v) => setForm({ ...form, long_term_debt: v })} />
            <NumField label="חלויות שוטפות" value={form.current_maturities} onChange={(v) => setForm({ ...form, current_maturities: v })} />
            <NumField label="בעלי מניות (התחייבויות)" value={form.shareholder_liabilities} onChange={(v) => setForm({ ...form, shareholder_liabilities: v })} />
            <NumField label="חברות קשורות (התחייבויות)" value={form.related_party_liabilities} onChange={(v) => setForm({ ...form, related_party_liabilities: v })} />
            <NumField label="הון עצמי" value={form.total_equity} onChange={(v) => setForm({ ...form, total_equity: v })} />
          </Section>

          <Section title="הון חוזר — פירוט">
            <NumField label="מקדמות לקוחות" value={form.working_capital_customer_advances} onChange={(v) => setForm({ ...form, working_capital_customer_advances: v })} />
            <NumField label="מקדמות לספקים" value={form.working_capital_supplier_advances} onChange={(v) => setForm({ ...form, working_capital_supplier_advances: v })} />
            <NumField label="הוצאות מראש" value={form.working_capital_prepaid_expenses} onChange={(v) => setForm({ ...form, working_capital_prepaid_expenses: v })} />
            <NumField label="הכנסות נדחות" value={form.working_capital_deferred_income} onChange={(v) => setForm({ ...form, working_capital_deferred_income: v })} />
            <NumField label="הוצאות לשלם" value={form.working_capital_accrued_expenses} onChange={(v) => setForm({ ...form, working_capital_accrued_expenses: v })} />
            <NumField label="הכנסות לקבל" value={form.working_capital_accrued_income} onChange={(v) => setForm({ ...form, working_capital_accrued_income: v })} />
          </Section>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              הערות
            </label>
            <textarea
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              rows={2}
              className="input"
            />
          </div>

          {create.isError && (
            <div className="flex items-start gap-2 p-3 mt-4 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
              <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <span>שגיאה: {create.error.message}</span>
            </div>
          )}

          <div className="flex justify-end gap-2 mt-6">
            <button
              type="button"
              onClick={() => {
                setForm(empty);
                setAdding(false);
              }}
              disabled={create.isPending}
              className="btn-secondary disabled:opacity-50"
            >
              ביטול
            </button>
            <button
              type="button"
              onClick={() => create.mutate(form)}
              disabled={!form.fiscal_year || create.isPending}
              className="btn-primary inline-flex items-center gap-2 disabled:opacity-50"
            >
              {create.isPending && <Loader2 className="w-4 h-4 animate-spin" />}
              שמור
            </button>
          </div>
        </div>
      )}

      {statementsQuery.isLoading ? (
        <div className="card text-center text-sm text-slate-500 py-8">טוען…</div>
      ) : statements.length === 0 ? (
        <div className="card text-center text-sm text-slate-500 py-8">
          אין דוחות לחברה הזאת. לחץ "דוח שנה" כדי להתחיל.
        </div>
      ) : (
        <StatementsGrid statements={statements} />
      )}
    </div>
  );
}

function StatementsGrid({ statements }: { statements: FinancialStatement[] }) {
  // Years descending — most recent on the right (LTR table) → leftmost in RTL view
  const sorted = useMemo(
    () => [...statements].sort((a, b) => b.fiscal_year - a.fiscal_year),
    [statements],
  );

  const ROWS: Array<{ section?: string; label?: string; key?: keyof FinancialStatement }> = [
    { section: 'דוח רווח והפסד' },
    { label: 'הכנסות', key: 'revenue' },
    { label: 'רווח גולמי', key: 'gross_profit' },
    { label: 'רווח תפעולי', key: 'operating_profit' },
    { label: 'רווח נקי', key: 'net_profit' },
    { label: 'פחת', key: 'depreciation' },
    { label: 'הוצאות מימון', key: 'financing_expense' },
    { label: 'מס', key: 'tax_expense' },
    { label: 'דיבידנד', key: 'dividend' },

    { section: 'מאזן — נכסים' },
    { label: 'סה״כ מאזן', key: 'total_assets' },
    { label: 'רכוש שוטף', key: 'current_assets' },
    { label: 'מזומנים', key: 'cash' },
    { label: 'לקוחות', key: 'receivables' },
    { label: 'מלאי', key: 'inventory' },
    { label: 'רכוש קבוע', key: 'fixed_assets' },
    { label: 'נכסים בלתי מוחשיים', key: 'intangible_assets' },
    { label: 'שיפורים במושכר', key: 'leasehold_improvements' },

    { section: 'מאזן — התחייבויות והון' },
    { label: 'ספקים', key: 'payables' },
    { label: 'חוב בנק ז״ק', key: 'short_term_debt' },
    { label: 'חוב בנק ז״א', key: 'long_term_debt' },
    { label: 'חלויות שוטפות', key: 'current_maturities' },
    { label: 'בעלי מניות', key: 'shareholder_liabilities' },
    { label: 'חברות קשורות', key: 'related_party_liabilities' },
    { label: 'הון עצמי', key: 'total_equity' },

    { section: 'הון חוזר' },
    { label: 'מקדמות לקוחות', key: 'working_capital_customer_advances' },
    { label: 'מקדמות לספקים', key: 'working_capital_supplier_advances' },
    { label: 'הוצאות מראש', key: 'working_capital_prepaid_expenses' },
    { label: 'הכנסות נדחות', key: 'working_capital_deferred_income' },
    { label: 'הוצאות לשלם', key: 'working_capital_accrued_expenses' },
    { label: 'הכנסות לקבל', key: 'working_capital_accrued_income' },
  ];

  return (
    <div className="card !p-0 overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-600">
            <tr>
              <th className="px-4 py-3 text-right font-medium sticky right-0 bg-slate-50">
                סעיף
              </th>
              {sorted.map((s) => (
                <th key={s.id} className="px-4 py-3 text-left font-medium num">
                  {s.fiscal_year}
                  {s.is_audited && (
                    <span className="text-xs text-slate-400 mr-1">(מבוקר)</span>
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {ROWS.map((row, i) => {
              if (row.section) {
                return (
                  <tr key={`s-${i}`} className="bg-slate-50">
                    <td
                      colSpan={1 + sorted.length}
                      className="px-4 py-2 text-xs font-semibold text-slate-600"
                    >
                      {row.section}
                    </td>
                  </tr>
                );
              }
              return (
                <tr key={`r-${i}`} className="hover:bg-slate-50">
                  <td className="px-4 py-2 text-slate-700 sticky right-0 bg-white">
                    {row.label}
                  </td>
                  {sorted.map((s) => (
                    <td key={s.id} className="px-4 py-2 text-left num">
                      {fmtMoney(row.key ? (s[row.key] as string | null) : null)}
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="mb-6">
      <h4 className="text-sm font-semibold text-slate-700 mb-3 pb-1 border-b border-slate-200">
        {title}
      </h4>
      <div className="grid md:grid-cols-4 gap-4">{children}</div>
    </div>
  );
}

function NumField({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <div>
      <label className="block text-sm text-slate-600 mb-1">{label}</label>
      <input
        type="number"
        step="0.01"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="input num"
      />
    </div>
  );
}
